import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaFileInvoiceDollar, FaDownload } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../../master/academicConfiguration/Academiclist.module.css";
import reportStyles from "./Report.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomDatePicker,
} from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  fetchAcademicYearOptions,
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../redux/Masterdata";
import { buildReportPdfHtml } from "./pdfReportTemplate";

/* ── ₹25,000 — amounts arrive as strings ("25000.00") ── */
const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);

/* ── "2026-05-05" -> "05/05/2026"; history dates already arrive formatted ── */
const formatDate = (val) => {
  if (!val) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return val;
};

const toApiDate = (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "");

const EMPTY_SUMMARY = {
  total_demand: 0,
  outstanding: 0,
  outstanding_percentage: 0,
};

/* CustomDropdown hands back the whole option (or null when cleared). */
const optionValue = (v) => (typeof v === "object" && v !== null ? v.value : v);

/* ────────────────────────────────────────────
   Expandable row panel — payment history.
──────────────────────────────────────────── */
const TransactionPanel = ({ row, labels }) => {
  const history = row?.original?.payment_history || [];

  if (history.length === 0) {
    return (
      <div className={reportStyles.panelEmpty}>
        {labels?.noHistoryFound || "No history found"}
      </div>
    );
  }

  return (
    <div className={reportStyles.panelWrap}>
      <table className={reportStyles.panelTable}>
        <thead>
          <tr>
            <th className={reportStyles.panelTh}>{labels?.date || "Date"}</th>
            <th className={reportStyles.panelTh}>
              {labels?.paymentMode || "Payment Mode"}
            </th>
            <th className={reportStyles.panelTh}>
              {labels?.upiTransactionId || "UPI Transaction ID"}
            </th>
            <th className={reportStyles.panelTh}>{labels?.paid || "Paid"}</th>
            <th className={reportStyles.panelTh}>
              {labels?.dueAmount || "Due Amount"}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.map((h, idx) => (
            <tr key={idx}>
              <td className={reportStyles.panelCell}>{formatDate(h.date)}</td>
              <td className={reportStyles.panelCell}>
                {h.payment_mode || "-"}
              </td>
              <td className={reportStyles.panelCell}>
                {h.upi_transaction_id || "-"}
              </td>
              <td className={reportStyles.panelCell}>
                {formatCurrency(h.paid)}
              </td>
              <td className={reportStyles.panelCell}>
                {formatCurrency(h.due_amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DueFeeReport = () => {
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);
  // memoised so the `|| {}` fallback doesn't create a new object each render
  // and re-trigger every useMemo/useCallback that depends on it
  const labels = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";

  const {
    academicYearOptions: reduxAcademicYearOptions,
    academicYearLoading,
    classOptions: reduxClassOptions,
    classLoading,
    sectionOptions: reduxSectionOptions,
    sectionLoading,
  } = useSelector((state) => state.masterData);

  // ── Filters ──
  const [academicYear, setAcademicYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // ── Grid ──
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [exporting, setExporting] = useState("");

  // ── Academic years on mount ──
  useEffect(() => {
    if (school_id) dispatch(fetchAcademicYearOptions(school_id));
  }, [dispatch, school_id]);

  // ── Default-select the active year (isActive === 1) once options land ──
  useEffect(() => {
    if (academicYear || reduxAcademicYearOptions.length === 0) return;
    const active =
      reduxAcademicYearOptions.find((o) => o.isActive === 1) ||
      reduxAcademicYearOptions[0];
    if (active) setAcademicYear(active.value);
  }, [reduxAcademicYearOptions, academicYear]);

  // ── The `id` behind the selected academic_year string — the report payload
  //    keys off the id, the class list off the label ──
  const academicYearId = useMemo(
    () =>
      reduxAcademicYearOptions.find((o) => o.value === academicYear)?.id || "",
    [reduxAcademicYearOptions, academicYear],
  );

  // ── Classes depend on the selected academic year ──
  useEffect(() => {
    if (!school_id || !academicYear) return;
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academicYear,
      }),
    );
  }, [dispatch, school_id, academicYear]);

  // ── Dropdown options — Class/Section carry an "All" entry ──
  const academicYearOptions = useMemo(
    () =>
      reduxAcademicYearOptions.map((o) => ({ value: o.value, label: o.label })),
    [reduxAcademicYearOptions],
  );

  const classOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...reduxClassOptions],
    [reduxClassOptions, labels],
  );

  const sectionOptions = useMemo(
    () => [{ value: "", label: labels.all || "All" }, ...reduxSectionOptions],
    [reduxSectionOptions, labels],
  );

  // ── Payload shared by the grid fetch and the two exports ──
  const buildPayload = useCallback(
    (page, limit) => ({
      school_id: Number(school_id),
      academic_year_id: Number(academicYearId),
      class_id: selectedClass ? Number(selectedClass) : "",
      section_id: selectedSection || "",
      start_date: toApiDate(fromDate),
      end_date: toApiDate(toDate),
      search: apiSearch.trim(),
      page,
      limit,
    }),
    [
      school_id,
      academicYearId,
      selectedClass,
      selectedSection,
      fromDate,
      toDate,
      apiSearch,
    ],
  );

  const transformRows = useCallback(
    (rows, offset = 0) =>
      rows.map((item, index) => ({
        s_no: offset + index + 1,
        emis_id: item.emis_id || item.admission_id || "",
        chalan_no: item.chellan_num || item.chalan_no || "",
        student_name: item.student_name || "",
        class_name: item.class_name || item.domain_id || "",
        section_id: item.section || item.section_id || "",
        group_name: item.group_name || "",
        paid_amount: Number(item.paid_amount) || 0,
        due_amount: Number(item.balance_amount ?? item.due_amount) || 0,
        discount_amount: Number(item.discount_amount) || 0,
        total_amount: Number(item.total_amount) || 0,
        due_date: formatDate(item.due_date),
        parent_mobile_no: item.parent_mobile_no || "",
        // the panel's "Due Amount" column is the running balance the API sends
        payment_history: Array.isArray(item.payment_history)
          ? item.payment_history.map((h) => ({
              ...h,
              due_amount: h.balance ?? h.due_amount,
            }))
          : [],
      })),
    [],
  );

  const fetchReport = useCallback(async () => {
    if (!school_id || !academicYearId) {
      setData([]);
      setRowCount(0);
      return;
    }

    setTableLoading(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/hm/dueFeesSummary",
        buildPayload(pagination.pageIndex + 1, pagination.pageSize),
      );

      const ok = response?.data?.status || response?.data?.success;
      if (ok && Array.isArray(response.data.data)) {
        setData(
          transformRows(
            response.data.data,
            pagination.pageIndex * pagination.pageSize,
          ),
        );
        setRowCount(
          response.data.pagination?.total || response.data.data.length,
        );
        setSummary({
          total_demand: Number(response.data.summary?.total_demand) || 0,
          outstanding: Number(response.data.summary?.outstanding) || 0,
          outstanding_percentage:
            Number(response.data.summary?.outstanding_percentage) || 0,
        });
      } else {
        setData([]);
        setRowCount(0);
        setSummary(EMPTY_SUMMARY);
      }
    } catch (error) {
      console.error("Error fetching due fee report:", error);
      setData([]);
      setRowCount(0);
      setSummary(EMPTY_SUMMARY);
    } finally {
      setTableLoading(false);
    }
  }, [
    school_id,
    academicYearId,
    buildPayload,
    transformRows,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ── Handlers ──
  const handleAcademicYearChange = useCallback(
    (value) => {
      const year = optionValue(value);
      if (!year) return; // required filter — never allowed to go empty
      setAcademicYear(year);
      setSelectedClass("");
      setSelectedSection("");
      dispatch(clearSectionOptions());
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [dispatch],
  );

  const handleClassChange = useCallback(
    (value) => {
      const classId = optionValue(value) || "";
      setSelectedClass(classId);
      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: master_id ? parseInt(master_id) : null,
            subject_staff: 1,
          }),
        );
      }
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleSectionChange = useCallback((value) => {
    setSelectedSection(optionValue(value) || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleFromDateChange = useCallback((d) => {
    setFromDate(d || null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleToDateChange = useCallback((d) => {
    setToDate(d || null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, []);

  // ── Exports pull the full filtered set, not just the visible page ──
  const fetchAllRows = useCallback(async () => {
    const response = await AxiosInstance.post(
      "schoolFees/hm/dueFeesSummary",
      buildPayload(1, rowCount || pagination.pageSize),
    );
    const ok = response?.data?.status || response?.data?.success;
    return ok && Array.isArray(response.data.data)
      ? transformRows(response.data.data)
      : [];
  }, [buildPayload, transformRows, rowCount, pagination.pageSize]);

  const exportHeaders = useMemo(
    () => [
      { key: "s_no", label: labels.serialNo || "S.No" },
      {
        key: "emis_id",
        label: labels.emisIdAdmissionId || "Emis ID / Admission ID",
      },
      { key: "chalan_no", label: labels.chalanNo || "Chalan No." },
      { key: "student_name", label: labels.studentName || "Student Name" },
      { key: "class_name", label: labels.class || "Class" },
      { key: "section_id", label: labels.section || "Section" },
      { key: "group_name", label: labels.groupName || "Group Name" },
      { key: "paid_amount", label: labels.paidAmount || "Paid Amount (₹)" },
      { key: "due_amount", label: labels.dueAmount || "Due Amount" },
      {
        key: "discount_amount",
        label: labels.discountAmount || "Discount Amount",
      },
      { key: "total_amount", label: labels.totalAmount || "Total Amount (₹)" },
      { key: "due_date", label: labels.dueDate || "Due Date" },
      {
        key: "parent_mobile_no",
        label: labels.parentsNumber || "Parents Number",
      },
    ],
    [labels],
  );

  const fileBaseName = useMemo(
    () =>
      `${labels.dueFeesReport || "Due Fees Report"}_${academicYear || ""}`.replace(
        /[^\w\-.]+/g,
        "_",
      ),
    [labels, academicYear],
  );

  const handleDownloadExcel = useCallback(async () => {
    setExporting("excel");
    try {
      const rows = await fetchAllRows();
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [
        exportHeaders.map((h) => escape(h.label)).join(","),
        ...rows.map((r) =>
          exportHeaders.map((h) => escape(r[h.key])).join(","),
        ),
      ].join("\r\n");

      // BOM keeps ₹ and Tamil labels readable when Excel opens the file
      const blob = new Blob([`﻿${csv}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileBaseName}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting due fee report to Excel:", error);
    } finally {
      setExporting("");
    }
  }, [fetchAllRows, exportHeaders, fileBaseName]);

  const handleDownloadPdf = useCallback(async () => {
    setExporting("pdf");
    try {
      const rows = await fetchAllRows();
      const { default: html2pdf } = await import("html2pdf.js");

      const container = document.createElement("div");
      container.style.padding = "16px";
      container.innerHTML = buildReportPdfHtml({
        title: labels.dueFeesReport || "Due Fees Report",
        infoItems: [
          clientData.school_name,
          `${labels.academicYear || "Academic Year"} ${academicYear}`,
          `${labels.reportDate || "Report Date"}: ${dayjs().format("DD/MM/YYYY")}`,
          toDate &&
            `${labels.dueDate || "Due Date"}: ${dayjs(toDate).format("DD/MM/YYYY")}`,
        ],
        headers: exportHeaders,
        rows,
      });

      await html2pdf()
        .set({
          margin: 8,
          filename: `${fileBaseName}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .from(container)
        .save();
    } catch (error) {
      console.error("Error exporting due fee report to PDF:", error);
    } finally {
      setExporting("");
    }
  }, [
    fetchAllRows,
    exportHeaders,
    fileBaseName,
    labels,
    academicYear,
    toDate,
    clientData.school_name,
  ]);

  // ── Summary card figures — the API sends the share, fall back to the ratio ──
  const outstandingPercent = useMemo(() => {
    if (summary.outstanding_percentage) return summary.outstanding_percentage;
    const total = Number(summary.total_demand) || 0;
    if (!total) return 0;
    return ((Number(summary.outstanding) || 0) / total) * 100;
  }, [summary]);

  // ── Columns ──
  const columns = useMemo(
    () => [
      {
        accessorKey: "emis_id",
        header: labels.emisIdAdmissionId || "Emis ID / Admission ID",
        size: 170,
        enableColumnFilter: false,
      },
      {
        accessorKey: "chalan_no",
        header: labels.chalanNo || "Chalan No.",
        size: 130,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_name",
        header: labels.studentName || "Student Name",
        enableColumnFilter: false,
      },
      {
        accessorKey: "class_name",
        header: labels.class || "Class",
        size: 100,
        enableColumnFilter: false,
      },
      {
        accessorKey: "section_id",
        header: labels.section || "Section",
        size: 110,
        enableColumnFilter: false,
      },
      {
        accessorKey: "group_name",
        header: labels.groupName || "Group Name",
        enableColumnFilter: false,
      },
      {
        accessorKey: "paid_amount",
        header: labels.paidAmount || "Paid Amount (₹)",
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "due_amount",
        header: labels.dueAmount || "Due Amount",
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "discount_amount",
        header: labels.discountAmount || "Discount Amount",
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "total_amount",
        header: labels.totalAmount || "Total Amount (₹)",
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "due_date",
        header: labels.dueDate || "Due Date",
        size: 120,
        enableColumnFilter: false,
      },
      {
        accessorKey: "parent_mobile_no",
        header: labels.parentsNumber || "Parents Number",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    ],
    [labels],
  );

  return (
    <div className={styles.container}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaFileInvoiceDollar />
        </div>
        <h1 className={styles.headerTitle}>
          {labels.searchDueFeesReport || "Search Due Fees Report"}
        </h1>
      </div>

      {/* ── Filters ── */}
      <div className={`row ${reportStyles.filterRow}`}>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.academicYear || "Academic Year"}
            name="academicYear"
            value={academicYear}
            onChange={handleAcademicYearChange}
            options={academicYearOptions}
            placeholder={labels.selectAcademicYear || "Select Academic Year"}
            isLoading={academicYearLoading}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.class || "Class"}
            name="class"
            value={selectedClass}
            onChange={handleClassChange}
            options={classOptions}
            placeholder={labels.selectClass || "Select Class"}
            isLoading={classLoading}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={labels.section || "Section"}
            name="section"
            value={selectedSection}
            onChange={handleSectionChange}
            options={sectionOptions}
            placeholder={labels.selectSection || "Select Section"}
            isLoading={sectionLoading}
            disabled={!selectedClass}
            required
            isClearable={false}
          />
        </div>
        <div className="col-lg-3 col-md-12 col-sm-12 px-2">
          <div className="row">
            <div className="col-6 px-1">
              <CustomDatePicker
                label={labels.dateRange || "Date Range"}
                name="fromDate"
                value={fromDate}
                onChange={handleFromDateChange}
                placeholder={labels.fromDate || "From Date"}
                format="DD/MM/YYYY"
                size="sm"
                maxDate={toDate || undefined}
              />
            </div>
            <div className="col-6 px-1">
              <CustomDatePicker
                label="&nbsp;"
                name="toDate"
                value={toDate}
                onChange={handleToDateChange}
                placeholder={labels.toDate || "To Date"}
                format="DD/MM/YYYY"
                size="sm"
                minDate={fromDate || undefined}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className={`row ${reportStyles.summaryRow}`}>
        <div className="col-lg-6 col-md-12 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardDemand}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueDemand}`}
              >
                {formatCurrency(summary.total_demand)}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.totalDemand || "Total Demand"}
              </span>
            </div>
            <div className={reportStyles.summaryFoot}>
              100% {labels.ofTotalDemand || "of Total Demand"}
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-12 px-2 mb-2">
          <div
            className={`${reportStyles.summaryCard} ${reportStyles.summaryCardOutstanding}`}
          >
            <div className={reportStyles.summaryCardTop}>
              <span
                className={`${reportStyles.summaryValue} ${reportStyles.summaryValueOutstanding}`}
              >
                {formatCurrency(summary.outstanding)}
              </span>
              <span className={reportStyles.summaryLabel}>
                {labels.outstanding || "Outstanding"}
              </span>
            </div>
            <div className={reportStyles.summaryFoot}>
              {outstandingPercent.toFixed(1)}%{" "}
              {labels.ofTotalDemand || "of Total Demand"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Export actions ── */}
      <div className={reportStyles.toolbar}>
        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownloadExcel}
          disabled={exporting !== "" || tableLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {exporting === "excel"
            ? labels.downloading || "Downloading..."
            : labels.downloadAsExcel || "Download as Excel"}
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownloadPdf}
          disabled={exporting !== "" || tableLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {exporting === "pdf"
            ? labels.downloading || "Downloading..."
            : labels.downloadAsPdf || "Download as PDF"}
        </CustomButton>
      </div>

      {/* ── Table ── */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={tableLoading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        placeholder={
          labels.searchByStudentNameChalanId ||
          "Search by student name, Chalan ID"
        }
        enableSearch={true}
        enableFiltering={false}
        manualPagination={true}
        enableExpanding={true}
        renderDetailPanel={({ row }) => (
          <TransactionPanel row={row} labels={labels} />
        )}
        localization={{
          noRecordsToDisplay: labels.noRecordsFound || "No records found",
        }}
      />
    </div>
  );
};

export default DueFeeReport;
